import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  isSectionVisible,
  resolveCustomerFormFieldName,
  resolveOptionsSourceKey,
} from "./customerDynamicFormApi";
import "../../common/commonStyles.css";

const SKIP_TYPES = new Set(["repeater", "relation_table", "display", "group"]);

const FIELD_ORDER = [
  "companyName",
  "firstName",
  "emailId",
  "phone",
  "route",
  "typeOfBusiness",
  "businessCategory",
  "default_delivery",
  "addressLine1",
  "addressLine2",
  "city",
  "county",
  "postalCode",
  "accound_no",
  "vatNo",
  "shiptoName",
  "shipLine1",
  "shipLine2",
  "shipCity",
  "shipPostcode",
  "shipState",
  "product_price",
  "credit_facility",
  "day_wise",
  "total_day",
  "invoice_wise",
  "total_invoice",
  "amount_wise",
  "total_amount",
  "end_of_month",
  "end_of_month_day",
  "lastName",
  "country",
  "isPo",
  "driving_license",
  "passport",
  "accept_terms",
];

const FIELD_LABEL_OVERRIDES = {
  companyName: "Business Name",
  firstName: "Customer Name",
  emailId: "Email Address",
  phone: "Phone",
  route: "Route",
  typeOfBusiness: "Type of Business",
  businessCategory: "Business Category",
  default_delivery: "Delivery Days",
  addressLine1: "Address",
  addressLine2: "Address 2",
  city: "City",
  county: "County",
  postalCode: "Postal Code",
  accound_no: "Account Number (User Name)",
  vatNo: "VAT Number",
};

const FULL_WIDTH_FIELDS = new Set(["product_price", "credit_facility"]);
const CREDIT_LIMIT_FIELDS = new Set([
  "day_wise",
  "total_day",
  "invoice_wise",
  "total_invoice",
  "amount_wise",
  "total_amount",
  "end_of_month",
  "end_of_month_day",
]);
const FORCE_SELECT_FIELDS = new Set([
  "route",
  "typeOfBusiness",
  "businessCategory",
  "default_delivery",
  "product_price",
]);

const placeCustomerNameAfterBusinessName = (fields) => {
  const list = [...fields];
  const businessIndex = list.findIndex(
    (field) => resolveCustomerFormFieldName(field.name) === "companyName",
  );
  const customerIndex = list.findIndex(
    (field) => resolveCustomerFormFieldName(field.name) === "firstName",
  );
  if (businessIndex < 0 || customerIndex < 0) return list;
  if (customerIndex === businessIndex + 1) return list;
  const [customerField] = list.splice(customerIndex, 1);
  const nextBusinessIndex = list.findIndex(
    (field) => resolveCustomerFormFieldName(field.name) === "companyName",
  );
  list.splice(nextBusinessIndex + 1, 0, customerField);
  return list;
};

const sortByPreviousOrder = (fields) => {
  const rank = new Map(FIELD_ORDER.map((key, index) => [key, index]));
  const sorted = fields
    .map((field, index) => ({ field, index }))
    .sort((a, b) => {
      const aKey = resolveCustomerFormFieldName(a.field.name);
      const bKey = resolveCustomerFormFieldName(b.field.name);
      const aRank = rank.has(aKey) ? rank.get(aKey) : 1000;
      const bRank = rank.has(bKey) ? rank.get(bKey) : 1000;
      if (aRank !== bRank) return aRank - bRank;
      return a.index - b.index;
    })
    .map(({ field }) => field);

  const seen = new Set();
  const unique = sorted.filter((field) => {
    const key = resolveCustomerFormFieldName(field.name);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return placeCustomerNameAfterBusinessName(unique);
};

const getOptionValue = (option) => {
  if (option == null) return "";
  if (typeof option !== "object") return String(option);
  return String(option.value ?? option.id ?? option.key ?? option.name ?? "");
};

const getOptionLabel = (option) => {
  if (option == null) return "";
  if (typeof option !== "object") return String(option);
  return String(
    option.label ?? option.name ?? option.title ?? option.route_number ?? getOptionValue(option)
  );
};

const PRICE_OPTIONS = [
  { label: "Price 1", value: "1" },
  { label: "Price 2", value: "2" },
  { label: "Price 3", value: "3" },
  { label: "Price 4", value: "4" },
  { label: "Price 5", value: "5" },
  { label: "Price 6", value: "6" },
];

const CREDIT_OPTIONS = [
  { label: "Credit Facility", value: "1" },
  { label: "Credit Facility Cash", value: "0" },
];

const END_OF_MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => String(i + 1));

const DynamicCustomerFormFields = ({
  sections = [],
  fields = [],
  formData = {},
  errors = {},
  onChange,
  onFileChange,
  isReadOnly = false,
  optionsBySource = {},
  disabledFields = {},
  fileNames = {},
}) => {
  const visibleSections = (sections || [])
    .map((section) => ({
      ...section,
      fields: (section.fields || []).filter(
        (field) => field.visible !== false && !SKIP_TYPES.has(field.type)
      ),
    }))
    .filter(
      (section) =>
        section.fields.length > 0 && isSectionVisible(section, formData)
    );

  const flatFields =
    visibleSections.length > 0
      ? null
      : (fields || []).filter(
          (field) => field.visible !== false && !SKIP_TYPES.has(field.type)
        );

  if (!visibleSections.length && !(flatFields || []).length) {
    return null;
  }

  const resolveOptions = (field) => {
    if (!field) return [];
    if (field.options?.length) return field.options;
    const sourceKey = resolveOptionsSourceKey(field.optionsSource);
    if (sourceKey && optionsBySource[sourceKey]) return optionsBySource[sourceKey];
    if (field.optionsSource && optionsBySource[field.optionsSource]) {
      return optionsBySource[field.optionsSource];
    }
    const formKey = resolveCustomerFormFieldName(field.name);
    const byFieldName = {
      route: "routes",
      typeOfBusiness: "businessTypes",
      businessCategory: "businessCategories",
      product_price: "priceGroups",
      default_delivery: "deliveryDays",
    };
    const mapped = byFieldName[formKey];
    return (mapped && optionsBySource[mapped]) || [];
  };

  const emitChange = (name, value) => {
    onChange?.({
      target: { name, value },
    });
  };

  const fieldGridSize = { xs: 12, sm: 6, md: 3 };
  const fieldByKey = new Map();

  const allFields = sortByPreviousOrder(
    visibleSections.length
      ? visibleSections.flatMap((section) => section.fields)
      : flatFields || []
  );
  allFields.forEach((field) => {
    fieldByKey.set(resolveCustomerFormFieldName(field.name), field);
  });

  const renderSelect = (field, formKey, value, error, readOnly, options) => (
    <FormControl
      className="form-input-border"
      fullWidth
      size="small"
      required={field.required}
      error={!!error}
      disabled={readOnly}
    >
      <InputLabel>{field.label}</InputLabel>
      <Select
        name={formKey}
        value={value === undefined || value === null ? "" : String(value)}
        label={field.label}
        onChange={(e) => emitChange(formKey, e.target.value)}
      >
        <MenuItem value="">All</MenuItem>
        {options.map((option) => (
          <MenuItem key={getOptionValue(option)} value={getOptionValue(option)}>
            {getOptionLabel(option)}
          </MenuItem>
        ))}
      </Select>
      {error ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
          {error}
        </Typography>
      ) : null}
    </FormControl>
  );

  const renderProductPrice = (field) => {
    const formKey = "product_price";
    const value = formData[formKey] ?? "";
    const options = resolveOptions(field);
    const radioOptions = options.length ? options : PRICE_OPTIONS;
    return (
      <FormControl component="fieldset" disabled={isReadOnly || field.readOnly}>
        <FormLabel component="legend" variant="caption" sx={{ fontWeight: 500, color: "#000" }}>
          Product Price
        </FormLabel>
        <RadioGroup
          row
          name={formKey}
          value={value}
          onChange={(e) => emitChange(formKey, e.target.value)}
          sx={{ marginTop: -1.5 }}
        >
          {radioOptions.map((option) => (
            <FormControlLabel
              key={getOptionValue(option)}
              value={getOptionValue(option)}
              control={<Radio className="checkBox" />}
              label={getOptionLabel(option)}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  };

  const renderCreditFacility = (field, cashOnly = false) => {
    const formKey = "credit_facility";
    const value = cashOnly ? "0" : (formData[formKey] ?? "0");
    const options = cashOnly
      ? CREDIT_OPTIONS.filter((option) => option.value === "0")
      : CREDIT_OPTIONS;
    return (
      <FormControl component="fieldset" disabled={isReadOnly || field?.readOnly}>
        {!cashOnly && (
          <FormLabel component="legend" variant="caption" sx={{ fontWeight: 500, color: "#000" }}>
            Credit Facility
          </FormLabel>
        )}
        <RadioGroup
          name={formKey}
          value={value}
          onChange={(e) => emitChange(formKey, e.target.value)}
          sx={{ marginTop: cashOnly ? 0 : -1.5 }}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio className="checkBox" />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  };

  const renderCreditLimits = () => {
    const dayField = fieldByKey.get("day_wise");
    const invoiceField = fieldByKey.get("invoice_wise");
    const amountField = fieldByKey.get("amount_wise");
    const endOfMonthChecked =
      formData.end_of_month === "1" || formData.end_of_month === true;

    const renderLimitBox = (checkField, totalKey, totalLabel) => {
      if (!checkField) return null;
      const checkKey = resolveCustomerFormFieldName(checkField.name);
      return (
        <Grid2 size={{ xs: 12, sm: 4 }}>
          <Box className="border-box" sx={{ p: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  className="checkBox"
                  name={checkKey}
                  checked={formData[checkKey] === "1"}
                  onChange={(e) => emitChange(checkKey, e.target.checked ? "1" : "0")}
                />
              }
              label={checkField.label}
            />
            {formData[checkKey] === "1" && fieldByKey.get(totalKey) && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  className="form-input-border"
                  fullWidth
                  size="small"
                  label={fieldByKey.get(totalKey)?.label || totalLabel}
                  name={totalKey}
                  type="number"
                  value={formData[totalKey] || ""}
                  onChange={(e) => emitChange(totalKey, e.target.value)}
                  error={!!errors[totalKey]}
                  helperText={errors[totalKey] || ""}
                  onWheel={(e) => e.target.blur()}
                />
              </Box>
            )}
          </Box>
        </Grid2>
      );
    };

    return (
      <Grid2 size={{ xs: 12, sm: 9 }}>
        <Grid2 container spacing={2}>
          {renderLimitBox(dayField, "total_day", "Total Days")}
          {renderLimitBox(invoiceField, "total_invoice", "Total Invoice")}
          {renderLimitBox(amountField, "total_amount", "Total Amount")}
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <Box className="border-box" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    className="checkBox"
                    name="end_of_month"
                    checked={endOfMonthChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      emitChange("end_of_month", checked ? "1" : "0");
                      if (!checked) emitChange("end_of_month_day", "");
                    }}
                  />
                }
                label="End of the Month"
              />
              {endOfMonthChecked ? (
                <Box sx={{ mt: 2 }}>
                  <FormControl
                    className="form-input-border"
                    fullWidth
                    size="small"
                    required
                    error={!!errors.end_of_month_day}
                  >
                    <InputLabel>Day</InputLabel>
                    <Select
                      name="end_of_month_day"
                      label="Day"
                      value={formData.end_of_month_day || ""}
                      onChange={(e) => emitChange("end_of_month_day", e.target.value)}
                    >
                      <MenuItem value="">Select Day</MenuItem>
                      {END_OF_MONTH_DAY_OPTIONS.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.end_of_month_day ? (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.end_of_month_day}
                      </Typography>
                    ) : null}
                  </FormControl>
                </Box>
              ) : null}
            </Box>
          </Grid2>
        </Grid2>
      </Grid2>
    );
  };

  const renderField = (field) => {
    const formKey = resolveCustomerFormFieldName(field.name);
    const fieldLabel = FIELD_LABEL_OVERRIDES[formKey] || field.label || formKey;
    const value = formData[formKey] ?? "";
    const error = errors[formKey] || "";
    const readOnly = isReadOnly || field.readOnly || !!disabledFields[formKey];
    let options = resolveOptions(field);

    if (formKey === "product_price") return renderProductPrice(field);
    if (formKey === "credit_facility") return renderCreditFacility(field);

    if (field.type === "file") {
      const accept = (field.accept || []).join(",");
      const inputId = `dyn_file_${formKey}`;
      return (
        <Box>
          <Typography className="productForm-lables" sx={{ mb: 0.5 }}>
            {fieldLabel}
            {field.required ? <span style={{ color: "red" }}> *</span> : null}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            className="button-variant-outlined"
            onClick={() => document.getElementById(inputId)?.click()}
            disabled={readOnly}
          >
            Choose File
          </Button>
          <input
            id={inputId}
            type="file"
            name={formKey}
            accept={accept || undefined}
            multiple={!!field.multiple}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              onFileChange?.(formKey, file);
            }}
          />
          <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {fileNames[formKey] || "No file chosen"}
          </Typography>
          {error ? (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          ) : null}
        </Box>
      );
    }

    if (field.type === "checkbox" && formKey !== "default_delivery") {
      return (
        <FormControlLabel
          control={
            <Checkbox
              className="checkBox"
              name={formKey}
              checked={value === "1" || value === 1 || value === true}
              onChange={(e) => emitChange(formKey, e.target.checked ? "1" : "0")}
              disabled={readOnly}
            />
          }
          label={
            <>
              {fieldLabel}
              {field.required ? <span style={{ color: "red" }}> *</span> : null}
            </>
          }
        />
      );
    }

    const useSelect =
      field.type === "select" ||
      field.type === "dropdown" ||
      (FORCE_SELECT_FIELDS.has(formKey) && options.length > 0) ||
      (formKey === "default_delivery" && (optionsBySource.deliveryDays || []).length > 0);

    if (formKey === "default_delivery" && useSelect) {
      options = options.length ? options : optionsBySource.deliveryDays || [];
      return renderSelect(
        { ...field, label: fieldLabel || "Delivery Days", required: field.required },
        formKey,
        value,
        error,
        readOnly,
        options
      );
    }

    if (useSelect) {
      return renderSelect(
        { ...field, label: fieldLabel },
        formKey,
        value,
        error,
        readOnly,
        options
      );
    }

    if (field.type === "checkbox") {
      return (
        <FormControlLabel
          control={
            <Checkbox
              className="checkBox"
              name={formKey}
              checked={value === "1" || value === 1 || value === true}
              onChange={(e) => emitChange(formKey, e.target.checked ? "1" : "0")}
              disabled={readOnly}
            />
          }
          label={fieldLabel}
        />
      );
    }

    const multiline = field.type === "textarea" || field.type === "multiline";
    const inputType =
      field.type === "number" ||
      field.type === "email" ||
      field.type === "tel" ||
      field.type === "password"
        ? field.type
        : "text";

    return (
      <TextField
        className="input-border text-field"
        fullWidth
        size="small"
        label={fieldLabel}
        name={formKey}
        type={inputType}
        value={value}
        placeholder={field.placeholder || ""}
        onChange={(e) => emitChange(formKey, e.target.value)}
        multiline={multiline}
        rows={multiline ? 2 : undefined}
        error={!!error}
        helperText={error}
        required={field.required}
        disabled={readOnly}
        inputProps={{
          maxLength: field.maxLength || undefined,
          min: field.min,
          max: field.max,
          step: field.step,
          readOnly,
        }}
        onWheel={inputType === "number" ? (e) => e.target.blur() : undefined}
      />
    );
  };

  const gridFields = allFields.filter((field) => {
    const key = resolveCustomerFormFieldName(field.name);
    return !FULL_WIDTH_FIELDS.has(key) && !CREDIT_LIMIT_FIELDS.has(key);
  });
  const productPriceField = fieldByKey.get("product_price");
  const creditFacilityField = fieldByKey.get("credit_facility");
  const showCreditLimits = String(formData.credit_facility) === "1";

  return (
    <Grid2 container spacing={3}>
      {gridFields.map((field) => (
        <Grid2 key={field.name} size={fieldGridSize}>
          {renderField(field)}
        </Grid2>
      ))}

      {productPriceField && (
        <Grid2 size={{ xs: 12 }}>{renderProductPrice(productPriceField)}</Grid2>
      )}

      <Grid2 size={{ xs: 12 }}>
        {renderCreditFacility(creditFacilityField, !creditFacilityField)}
      </Grid2>

      {showCreditLimits && renderCreditLimits()}
    </Grid2>
  );
};

export default DynamicCustomerFormFields;
